/* eslint-disable no-useless-escape */
import { useVuelidate } from "@vuelidate/core";
import { isFieldVisible } from "@elements/formFields/utils";
import {
  required,
  requiredIf,
  //minLength,
  //maxLength,
  minValue,
  maxValue,
  sameAs,
  email,
  url,
  helpers
} from "@vuelidate/validators";

import { get, forEach, cloneDeep } from "lodash-es";

import {
  dateValidation,
  ibanValidation,
  bankAccountValidation,
  //emailValidation,
  priceDistributionValidation,
  //urlValidation,
  customRegexValidation,
  businessRegistryCodeValidation,
  personRegistryCodeValidation,
  swiftBicValidation,
  vatCodeValidation
} from "@/mixins/custom_validation";

/**
 *
 * @param {any} value field value itself
 * @param {string} propName property name that depends on whether this field is required or not
 * @param {array} requiredWhenValues values that should return value true based on propName property
 * @param {object} siblings form object
 * @returns boolean - false will trigger validation error
 */
function requiredWhenPropHasCondition(
  value,
  propName,
  requiredWhenValues,
  siblings
) {
  if (!requiredWhenValues || requiredWhenValues.length == 0) return true;
  if (
    (siblings[propName] != undefined || siblings[propName] != null) &&
    requiredWhenValues.includes(siblings[propName])
  ) {
    return value != null && value != undefined ? true : false;
  }
  return true;
}

/**
 * Dedicated to workaround uploaded file that has *Path (like incomeTaxCardFilePath) value
 * should not give out error over *File (like incomeTaxCardFile)
 * Also checks if selected to remove, then check if there is new file selected
 *
 * @param {any} value eg incomeTaxCardFile property value
 * @param {object} siblings form's other properties
 * @param {object} fieldNames
 * filePathName {string} - name of field that should contain path to file.
 *
 * fileRemoveName {boolean} - name of field that determines whether file is to be deleted;
 *
 * @returns boolean for vuelidate's requiredIf built-in validator
 *
 * true if marked for delete but no new file added or there just is no file seletced
 *
 * false if file is selected or has *Path field value.
 */
function fileRequiredCondition(
  value,
  siblings,
  { filePathName, fileRemoveName }
) {
  if (siblings[fileRemoveName] === true && !value) return true; // return true so requiredIf will validate the need to have new file selected
  if (siblings[filePathName]) return false; // return false so requiredIf will not validate it
  return !value ? true : false; // if no value, return true so requiredIf will validate. otherwise false
}

/**
 * Replaces required and requiredWhenPropHas to requiredIf, where it checks if its required and then
 * checks if file has been selected or has url path instead
 *
 * NB! cloneDeep whole vuelidateValidations object or original data will dissapear!
 *
 * @param {string} propertyKey field name to get value from siblings object
 * @param {object} ruleValidation rule validations from server how to validate field
 * @param {object} siblings form object
 * @param {object} param3
 * filePathName - field name to get file path value (like incomeTaxCardFilePath to indicate file is uploaded);
 *
 * fileRemoveName - field name (incomeTaxCardFileRemove) to indicate if set to be removed
 * @returns ruleValidation that replaces required and requiredWhenHasProps to requiredIf
 */
export function injectFileRequiredCondition(
  propertyKey,
  ruleValidation,
  siblings,
  { filePathName, fileRemoveName }
) {
  let isRequired = ruleValidation.required ?? false;

  if (!isRequired && ruleValidation.requiredWhenPropHas) {
    const { propName, values } = ruleValidation.requiredWhenPropHas;
    isRequired = !requiredWhenPropHasCondition(
      siblings[propertyKey],
      propName,
      values,
      siblings
    ); // function returns false as needed to validate. Its why there's ! prepended
  }

  ruleValidation.requiredIf = () => {
    if (!isRequired) return false; // when its not required at all, return false so requiredIf won't validate
    return fileRequiredCondition(siblings[propertyKey], siblings, {
      filePathName,
      fileRemoveName
    });
  };

  delete ruleValidation.required;
  delete ruleValidation.requiredWhenPropHas;

  return ruleValidation;
}

export default {
  data() {
    return {
      /** eg horses:horseApplication which then gets appended with field name and .label */
      appFormMixinI18MainKey: null,
      /** If field's i18n key is too different to be able to just take appFormMixinI18MainKey and append its name, then try getting
       * from a computed/ref/object by field name
       */
      appFormMixinCustomFieldLabels: null,
      appFormMixinFormTracker: "form",
      formError: {},
      appFormMixinValidationsMapToModel: {}
    };
  },
  // exterme scenario here to pass 3rd option
  // vueldiateConfig did not work
  setup(props = {}, vuelidateConfig = {}) {
    return {
      v$: useVuelidate(null, null, vuelidateConfig)
    };
  },
  validations() {
    return this.appFormMixinPrepareValidations(
      get(this, this.appFormMixinValidationsConfVariable, {}) ?? {}
    );
  },
  methods: {
    appFormMixinIsFieldHidden(fieldName) {
      const items = Object.keys(
        get(this, this.appFormMixinValidationsConfVariable, {}) ?? {}
      );
      // Return false means field is hidden
      return items.includes(fieldName);
    },
    appFormMixinIsFieldDisabled(fieldName) {
      return (
        get(this, this.appFormMixinReadOnlyFieldsVariable, []) ?? []
      ).includes(fieldName);
    },
    appFormMixinPrepareValidations(validationConf) {
      const { withParams, withMessage, withAsync } = helpers;
      const config = cloneDeep(validationConf);
      // console.log("Preparing validation conf", validationConf);
      forEach(
        this.appFormMixinValidationsMapToModel,
        function (modelFieldName, fieldName) {
          //console.log("Mapping fields", fieldName, modelFieldName);
          if (config[fieldName]) {
            config[modelFieldName] = config[fieldName]; // Assign new key
            delete config[fieldName]; // Delete old key
          }
        }
      );

      const formToTrack = this.appFormMixinFormTracker;
      const form = {};

      if ("incomeTaxCardFile" in config) {
        config.incomeTaxCardFile = injectFileRequiredCondition(
          "incomeTaxCardFile",
          config.incomeTaxCardFile,
          this[formToTrack],
          {
            filePathName: "incomeTaxCardFilePath",
            fileRemoveName: "incomeTaxCardFileRemove"
          }
        );
      }

      const formFields =
        get(
          this,
          this.appFormMixinValidationsConfVariable
            ? this.appFormMixinValidationsConfVariable.split(".")[0] +
                ".formFields"
            : "",
          []
        ) ?? [];

      // console.log("Validation conf after mapping", validationConf);
      Object.entries(config).forEach(([key, value]) => {
        form[key] = {};

        if (this.appFormMixinIsFieldDisabled(key)) return;

        let formField = formFields.find(x => x.name == key);
        if (formField && !isFieldVisible(this.form, formField)) {
          return;
        }

        const validationRules = {};
        if (value.validationRegex) {
          const regexPattern = value.validationRegex;
          validationRules.validationRegex = withMessage(
            ({ $model }) =>
              this.$t("common:formValidation.invalid", "Invalid") +
              " " +
              $model,
            withParams({ type: "validationRegex" }, validationValue =>
              customRegexValidation(validationValue, regexPattern)
            )
          );
        }

        if (value.email || value.emailValidation) {
          validationRules.email = withMessage(
            () =>
              this.$t(
                "common:formValidation.emailValidation",
                "Invalid email format"
              ),
            email
          );
        }

        if (typeof value.requiredIf === "string") {
          const requiredIfFieldName = value.requiredIf;
          validationRules.requiredIfPropHas = withMessage(
            () =>
              this.$t(
                "common:formValidation.requiredField",
                "Should be filled"
              ),
            withParams({}, (v, siblings) => {
              if (v || v === 0) return true;
              if (!!siblings[requiredIfFieldName] === true) return false;
              return true;
            })
          );
        } else if (value.requiredIf) {
          validationRules.requiredIf = withMessage(
            () =>
              this.$t(
                "common:formValidation.requiredField",
                "Should be filled"
              ),
            requiredIf(value.requiredIf)
          );
        }

        if (value.requiredWhenPropHas) {
          const { propName, values } = value.requiredWhenPropHas;
          validationRules.requiredWhenPropHas = withMessage(
            () =>
              this.$t(
                "common:formValidation.requiredField",
                "Should be filled"
              ),
            withParams({}, (v, siblings) =>
              requiredWhenPropHasCondition(v, propName, values, siblings)
            )
          );
        }

        if (value.required) {
          validationRules.required = withMessage(
            () =>
              this.$t(
                "common:formValidation.requiredField",
                "Should be filled"
              ),
            required
          );
        }

        if (value.vatCode) {
          validationRules.vatCode = withAsync(
            withMessage(
              () =>
                this.$t("common:formValidation.vatCode", "Invalid VAT code"),
              (value, siblings) => vatCodeValidation(value, siblings)
            ),
            () => this.form?.registryCountryId // watches this property changes to force trigger this validation again
          );
        }

        if (value.dateValidation) {
          validationRules.dateValidation = withParams(
            {
              type: "dateValidation"
            },
            dateValidation
          );

          if (value.maxValue) {
            const max = Date.parse(value.maxValue);
            validationRules.maxValue = withMessage(
              ({ $params }) => {
                if (this.$dayjs().format("YYYY-MM-DD") == $params.maxISO) {
                  return this.$t(
                    "common:formValidation.dateValidation.cannotBeInFuture",
                    "Date cannot be in future"
                  );
                } else {
                  return this.$t(
                    [
                      "common:formValidation.dateValidation.cannotBeLaterThan",
                      "Date cannot be later than {{ maxValue }}"
                    ],
                    {
                      maxValue: this.$dayjs.date($params.maxISO),
                      interpolation: { escapeValue: false }
                    }
                  );
                }
              },
              withParams(
                {
                  type: "maxValue",
                  max: max,
                  maxISO: value.maxValue
                },
                v => (!v ? true : Date.parse(v) <= max)
              )
            );
          }

          if (value.minValue) {
            const min = Date.parse(value.minValue);
            validationRules.minValue = withMessage(
              () => {
                return this.$t(
                  "common:formValidation.dateValidation.cannotBeInPast",
                  "Date cannot be in past"
                );
              },
              withParams(
                {
                  type: "minValue",
                  min: min,
                  minISO: value.minValue
                },
                v => (!v ? true : Date.parse(v) >= min)
              )
            );
          }
        } else {
          if (value.maxValue || value.maxValue == 0) {
            validationRules.maxValue = withMessage(
              () =>
                this.$t("common:formValidation.maxValue", `Number max reached`),
              maxValue(value.maxValue)
            );
          }

          if (value.minValue || value.minValue == 0) {
            if (typeof value.minValue === "object" && value.minValue.minValue) {
              let obj = value.minValue;
              validationRules.minValue = withMessage(
                () => obj.message,
                minValue(obj.minValue)
              );
            } else {
              validationRules.minValue = withMessage(
                () =>
                  this.$t(
                    "common:formValidation.minValue",
                    `Number cannot go futher down`
                  ),
                minValue(value.minValue)
              );
            }
          }
        }

        if (value.priceDistributionValidation) {
          validationRules.priceDistributionValidation = withMessage(
            () =>
              this.$t(
                "common:formValidation.priceDistributionValidation",
                "Price distribution cannot contain letters"
              ),
            withParams(
              {
                type: "priceDistributionValidation"
              },
              priceDistributionValidation
            )
          );
        }

        if (value.minLength) {
          const min = value.minLength;
          validationRules.minLength = withMessage(
            ({ $params }) =>
              this.$t(
                [
                  "common:formValidation.minLength",
                  `Should be at least {\{ minLength }\} characters long`
                ],
                {
                  minLength: $params.min
                }
              ),
            withParams(
              {
                type: "minLength",
                min: min
              },
              v => {
                if (!v) return true;
                else if (Array.isArray(v)) return v.length >= min;
                return v.replace(/<[^>]*>/g, "").length >= min; // Exclude html tags
              }
            )
          );
        }

        if (value.maxLength) {
          const max = value.maxLength;
          validationRules.maxLength = withMessage(
            ({ $params }) =>
              this.$t(
                [
                  "common:formValidation.maxLength",
                  `Cannot be over {\{ maxLength }\} characters long`
                ],
                {
                  maxLength: $params.max
                }
              ),
            withParams(
              {
                type: "maxLength",
                max: max
              },
              v => {
                if (!v) return true;
                else if (Array.isArray(v)) return v.length <= max;
                return v.replace(/<[^>]*>/g, "").length <= max; // Exclude html tags
              }
            )
          );
        }

        if (value.ibanValidation == true) {
          validationRules.ibanValidation = withMessage(
            () =>
              this.$t(
                "common:formValidation.ibanValidation",
                "Invalid IBAN format."
              ),
            withParams({ type: "ibanValidation" }, ibanValidation)
          );
        }

        if (value.bankAccountValidation == true) {
          validationRules.bankAccountValidation = withMessage(
            () =>
              this.$t(
                "common:formValidation.bankAccountValidation",
                "Invalid BBAN or IBAN format."
              ),
            withParams({ type: "bankAccountValidation" }, (value, siblings) =>
              bankAccountValidation(value, siblings)
            )
          );
        }

        if (value.personRegistryCode == true) {
          validationRules.personRegistryCode = withMessage(
            () =>
              this.$t(
                "common:formValidation.personRegistryCodeValidation",
                `Invalid personal registry code`
              ),
            withParams({ type: "personRegistryCode" }, (value, siblings) =>
              personRegistryCodeValidation(value, siblings)
            )
          );
        }

        if (value.organisationRegistryCode == true) {
          validationRules.organisationRegistryCode = withMessage(
            () =>
              this.$t(
                "common:formValidation.organisationRegistryCode",
                `Invalid business registry code`
              ),
            withParams(
              { type: "organisationRegistryCode" },
              (value, siblings) =>
                businessRegistryCodeValidation(value, siblings)
            )
          );
        }

        if (value.swiftBicCode) {
          validationRules.swiftBicCode = withMessage(
            () => this.$t("common:formValidation.swiftBicCode", `Invalid BIC`),
            withParams(
              { type: "swiftBicCode" },

              swiftBicValidation
            )
          );
        }

        if (value.url == true) {
          validationRules.url = withMessage(
            () => this.$t("common:formValidation.url", "Invalid URL"),
            url
          );
        }

        if (value.sameAsPassword) {
          validationRules.sameAsPassword = withMessage(
            () =>
              this.$t(
                "signin:newpassword.passwordsDoNotMatch",
                "Passwords do not match"
              ),
            sameAs(this.form[value.sameAsPassword])
          );
        }

        // Filter values that are null
        Object.entries(validationRules).forEach(([valKey, val]) => {
          if (val === null) return;
          form[key][valKey] = val;
        });
      });

      return { [formToTrack]: form };
    },

    appFormMixinValidationErrorsForField(fieldName, type) {
      if ((this.formError ?? true) === true) {
        this.formError = {};
      }

      const form = this.v$[this.appFormMixinFormTracker ?? "form"];

      // this.appFormMixinValidationsConfVariable returns the path where it checks if there are any validation configuration from server/data()
      if (
        !get(this, this.appFormMixinValidationsConfVariable, false) ||
        (form[fieldName] ?? false) == false
      ) {
        if ((form[fieldName] ?? true) == true) {
          console.error(
            `[${this.$vnode.componentOptions.tag}] Tried to check fieldName ${fieldName} but doesn't exist in v$!`
          );
        }
        return [];
      }

      if (
        form[fieldName].$invalid == false ||
        form[fieldName].$dirty == false
      ) {
        // Remove from formError
        delete this.formError[fieldName];
        return [];
      }

      const errors = form[fieldName].$errors.map(x => x.$message);

      if (errors.length > 0) {
        this.$set(this.formError, fieldName, {
          // "this" here refers to this.formError[fieldName]
          get: function () {
            return this.errors;
          },
          errors: errors,
          translatedFieldName: this.appFormMixinCustomFieldLabels
            ? get(
                this,
                this.appFormMixinCustomFieldLabels + `[${fieldName}]`,
                null
              )
            : null,
          i18nKey: this.appFormMixinI18MainKey
            ? this.appFormMixinI18MainKey + `.${fieldName}.label`
            : null
        });
      } else {
        this.$delete(this.formError, fieldName);
      }

      return errors;
    }
  }
};
